import {
    ChangeEvent,
    useMemo,
    useRef,
    useState,
    useCallback,
    useEffect,
    ReactNode,
    SyntheticEvent,
    FocusEvent,
    MouseEvent,
    MouseEventHandler,
    useContext
} from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Autocomplete, { AutocompleteProps, AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import { IconButton, styled, SxProps, useMediaQuery, useTheme } from '@mui/material';
import LoopIcon from '@/assets/icons/loop.svg';
import LoopIconBlack from '@/assets/icons/loop-black.svg';
import MicrophoneIcon from '@/assets/icons/microphone.svg';
import MicrophoneSolidIcon from '@/assets/icons/microphone-solid.svg';
import PlusIcon from '@/assets/icons/plus-circle-green.svg';
import { VoiceRecognitionPopup } from '@/shared/components/voice-recognition-popup';
import { useAppDispatch, useAppSelector } from '@/shared/hooks/store';
import { addHelperShown } from '@/store/slices/auth';
import { selectHelpersShown } from '@/store/slices/auth/selectors';
import { userDetails } from '@/store/slices/onboarding/selectors';
import { IntercomContext } from '../../../App';

import Modal from '../modal';

import './index.css';
import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook';
import { GtmEvent } from '@/shared/types/enums';

interface ISelectOption<TOption> {
    freeContent?: ReactNode;
    label: string;
    value: TOption;
}

export interface InputPath {
    medium: string;
    timestamp: Date;
}

export type AutoCompleteInputVariant = 'main' | 'advanced';

export interface IAutoCompleteProps<TOption>
    extends Omit<
        AutocompleteProps<ISelectOption<TOption>, false, false, false>,
        'renderInput' | 'options' | 'value' | 'onSelect' | 'onChange'
    > {
    label?: string;
    subLabel?: ReactNode | string;
    placeholder?: string;
    required?: boolean;
    labelKey?: keyof TOption;
    freeContentKey?: keyof TOption;
    loading?: boolean;
    onSelect: (value: TOption, label: string) => void;
    loadOptionsList: (query: string, signal: AbortSignal) => Promise<TOption[] | null> | TOption[]
    needDict?: boolean;
    helperTimeout?: number;
    startAdornmentSx?: SxProps;
    withVoiceHelper?: boolean;
    helperArrowClassName?: string;
    popover?: boolean;
    onPopoverClose?(): void;
    uniqueIdentifier: string;
    error?: boolean;
    clearOnBlur?: boolean;
    value?: string;
    onChange?(newValue: string): void;
    keepSelectedValue?: boolean;
    defaultVoiceOpen?: boolean;
    scrollToTitle?: boolean;
    extraOptions?: TOption[];
    popoverHelper?: ReactNode;
    labelClassName?: string;
    inputVariant?: AutoCompleteInputVariant;
    popoverTitle?: string;
    inputWithBorder?: boolean;
    onVoiceResult?(value: string): void;
    onMicClick?(): void;
    searched?: boolean;
}

const CustomLoopIcon = styled('img')({
    width: '26px',
    height: '26px'
});

const uniqueIdentifierToUiText = {
    conditionsFinder: 'conditions',
    allergiesFinder: 'allergies',
    pharmacyFinder: 'pharmacies',
    providerFinder: 'providers',
    medicationFinder: 'medications'
};

export function AutoComplete<TOption>(allProps: IAutoCompleteProps<TOption>) {
    const {
        label,
        labelKey,
        freeContentKey,
        loadOptionsList,
        subLabel,
        required,
        placeholder,
        onSelect,
        needDict = true,
        helperTimeout = 6000,
        startAdornmentSx,
        uniqueIdentifier,
        popover,
        error,
        onPopoverClose,
        clearOnBlur = true,
        value: propsSearchValue,
        keepSelectedValue,
        defaultVoiceOpen = false,
        extraOptions = [],
        popoverHelper,
        onChange,
        labelClassName,
        inputVariant = 'main',
        inputWithBorder,
        onVoiceResult,
        onMicClick,
        initialIntent,
        popoverTitle,
        searched,
        ...props
    } = allProps;
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const sendDataToGTM = useGTMDispatch();
    const rawHelpersShown = useAppSelector(selectHelpersShown);
    const user = useAppSelector(userDetails);
    const helpersShown = useMemo(() => rawHelpersShown || [], [rawHelpersShown]);
    const autocompleteRef = useRef<HTMLElement>(null);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const initRef = useRef(false);
    const micButtonRef = useRef<HTMLButtonElement | null>(null);

    const [isOpen, setIsOpen] = useState(false);
    const [_searchValue, setSearchValue] = useState<string>('');
    const searchValue = propsSearchValue || _searchValue; //controlled/uncontrolled
    const [isVoiceRecognitionOpened, setIsVoiceRecognitionOpened] = useState(defaultVoiceOpen);
    const [optionsList, setOptionsList] = useState<TOption[]>([]);
    const [isEmpty, setIsEmpty] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [isHelperOpened, setIsHelperOpened] = useState(false);
    const [isVoiceInput, setIsVoiceInput] = useState(false);
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const [collectedStrings, setCollectedStrings] = useState<string[]>([]);
    const [inputPath, setInputPath] = useState<InputPath | null>(null);
    const helperOpened = isHelperOpened && (!isMobile || popover) && !isVoiceRecognitionOpened;
    const intercomContext = useContext(IntercomContext);

    const latestAbortControllerRef = useRef<AbortController | null>(null);

    const isHelperUsed = useMemo(
        () => Array.isArray(helpersShown) && helpersShown.includes(uniqueIdentifier),
        [helpersShown, uniqueIdentifier]
    );

    const popoverFieldsetSx = useMemo(
        () =>
            popover
                ? {
                    border: 'none',
                    borderBottom: isFocused ? '2px solid #02836F !important' : '1px solid rgba(0, 0, 0, 0.23) !important',
                    borderRadius: '0'
                }
                : {},
        [isFocused, popover]
    );

    const inputSX: Record<AutoCompleteInputVariant, SxProps> = useMemo(
        () => ({
            advanced: {
                padding: popover ? '0 10px' : 0,
                position: 'relative',
                '.MuiInputBase-root fieldset': {
                    border: 'none'
                },
                '& > .MuiInputBase-root': {
                    p: '0 !important',
                    height: '53px',
                    justifyContent: 'start !important'
                },
                '& > .MuiInputBase-root > input': {
                    height: '100%',
                    flex: '1 1 auto',
                    border: inputWithBorder ? '2px solid' : 'none',
                    borderColor: error ? 'red' : 'primary.main',
                    borderRight: 'none',
                    borderRadius: '7px 0 0 7px',
                    boxSizing: 'border-box',
                    position: 'relative',
                    marginLeft: '5px',
                },
                '&:after': {
                    content: '""',
                    display: !inputWithBorder && error ? 'block' : 'none',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: '100%',
                    height: '100%',
                    borderRadius: '7px 0 0 7px',
                    borderRight: 'none',
                    border: '2px solid',
                    bgcolor: 'transparent',
                    zIndex: 0,
                    borderColor: error ? 'red' : 'primary.main'
                }
            },
            main: {
                width: '100%',
                bgcolor: 'white',
                zIndex: 20,
                fieldset: {
                    border: error ? '1px solid red' : '1px solid transparent',
                    borderRadius: '10px',
                    ...popoverFieldsetSx
                },
                '& > .MuiInputBase-root': {
                    overflow: 'hidden',
                    padding: '10px 17px !important',
                    [theme.breakpoints.down('md')]: {
                        padding: '5px 7px !important'
                    }
                },
                '.MuiInputBase-root.Mui-focused fieldset': {
                    borderColor: 'primary.main'
                },
                '.MuiInputBase-root fieldset:hover': {
                    borderColor: 'black'
                }
            }
        }),
        [error, popover, popoverFieldsetSx, theme, inputWithBorder]
    );

    const updateOptionsList = useCallback((list: TOption[]) => {
        setOptionsList(list);
        setIsOpen(!!list.length);
    }, []);

    const handleCloseHelper = useCallback(() => {
        setIsHelperOpened(false);
    }, [dispatch]);

    const handleShowHelper = useCallback(() => {
        if (!isHelperUsed && needDict) {
            dispatch(addHelperShown(uniqueIdentifier));

            if (!defaultVoiceOpen) {
                setIsHelperOpened(true);
                setTimeout(handleCloseHelper, helperTimeout);
            }
        }
    }, [dispatch, isHelperUsed, handleCloseHelper, helperTimeout, needDict, defaultVoiceOpen, uniqueIdentifier]);

    const makeSearch = useCallback(
        async (value: string) => {
            if (!value) {
                setIsEmpty(false);
                updateOptionsList([]);
            } else {

                if (latestAbortControllerRef.current) {
                    latestAbortControllerRef.current.abort();
                }

                const controller = new AbortController();
                latestAbortControllerRef.current = controller;
                const formattedQuery = value.trim();
                const data = await loadOptionsList(formattedQuery, controller.signal);

                if (!controller.signal.aborted) {
                    setIsEmpty(!data?.length && value.length >= 2);
                    updateOptionsList(data || []);
                }
            }
        },
        [loadOptionsList, updateOptionsList]
    );

    const sendGTMEventUserGaveUp = useCallback(
        (collectedStringsInput: string[], collectedInputPathMedium: string) => {
            // console.log({
            //     event: GtmEvent.AUTO_COMPLETE_USER_GAVE_UP,
            //     category: uniqueIdentifier,
            //     inputPath: collectedInputPathMedium,
            //     userId: user?.id || '',
            //     collectedStrings: collectedStringsInput.join('|')
            // })
            sendDataToGTM({
                event: GtmEvent.AUTO_COMPLETE_USER_GAVE_UP,
                category: uniqueIdentifier,
                inputPath: collectedInputPathMedium,
                userId: user?.id || '',
                collectedStrings: collectedStringsInput.join('|')
            });
        },
        [sendDataToGTM, uniqueIdentifier, user?.id]
    );

    const handleChange = useCallback(
        async (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
            if (timerRef.current) clearTimeout(timerRef.current);
            setIsHelperOpened(false);

            const { value } = e.currentTarget;

            if (value.length <= 1) {
                latestAbortControllerRef.current?.abort();
                updateOptionsList([]);
            }
            initRef.current = true;

            if (value.length >= 2) {
                if (inputPath?.medium === 'keyboard') {
                    if (value.length > searchValue.length) {
                        if (collectedStrings.length > 0) {
                            if (value.startsWith(collectedStrings[collectedStrings.length - 1])) {
                                setCollectedStrings([...collectedStrings.slice(0, collectedStrings.length - 1), value]);
                            } else {
                                setCollectedStrings([...collectedStrings, value]);
                            }
                        } else {
                            setCollectedStrings([value]);
                        }
                    }
                } else {
                    setInputPath({ medium: 'keyboard', timestamp: new Date() });
                    // console.log({
                    //     event: GtmEvent.AUTO_COMPLETE_INPUT_INITIATED,
                    //     category: uniqueIdentifier,
                    //     inputPath: 'keyboard',
                    //     userId: user?.id || ''
                    // })
                    sendDataToGTM({
                        event: GtmEvent.AUTO_COMPLETE_INPUT_INITIATED,
                        category: uniqueIdentifier,
                        inputPath: 'keyboard',
                        userId: user?.id || ''
                    });
                }
                timerRef.current = setTimeout(() => makeSearch(value), 400);
            } else if (collectedStrings.length > 0) {
                sendGTMEventUserGaveUp(collectedStrings, inputPath?.medium || '');
                setCollectedStrings([]);
            }

            setSearchValue(value);
            onChange?.(value);
            setIsVoiceInput(false);
        },
        [collectedStrings, inputPath, makeSearch, onChange, sendDataToGTM, uniqueIdentifier, user?.id, searchValue, sendGTMEventUserGaveUp]
    );

    const optionsListFormatted = useMemo(() => {
        if (!labelKey) return [];

        if (isEmpty && !extraOptions?.length)
            return [
                {
                    label: 'No matches found',
                    value: -1 as TOption
                }
            ];

        return [...optionsList, ...extraOptions].map(option => ({
            label: option[labelKey] as string,
            freeContent: freeContentKey && option[freeContentKey],
            value: option
        }));
    }, [optionsList, extraOptions, labelKey, freeContentKey, isEmpty]);

    const toggleVoiceRecognitionPopup = useCallback(() => {
        dispatch(addHelperShown(uniqueIdentifier));
        setIsVoiceRecognitionOpened(prev => !prev);
    }, [uniqueIdentifier, dispatch]);

    const handleVoiceResult = useCallback(
        (voiceResult: string) => {
            initRef.current = true;
            setSearchValue(voiceResult);
            setIsVoiceRecognitionOpened(false);
            setIsVoiceInput(true);
            setCollectedStrings([voiceResult]);
            timerRef.current = setTimeout(() => makeSearch(voiceResult), 100);
            onVoiceResult?.(voiceResult);
        },
        [makeSearch, onVoiceResult]
    );

    useEffect(() => {
        if (initialIntent === 'microphone') {
            const timer = setTimeout(() => micButtonRef?.current?.click(), 300);
            return () => {
                clearTimeout(timer);
            };
        }
    }, []);

    const handleOpenVoice: MouseEventHandler<HTMLButtonElement> = useCallback(
        (e: MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            if (isMobile && !popover) {
                onMicClick?.();
                return;
            }
            setInputPath({ medium: 'microphone', timestamp: new Date() });
            sendDataToGTM({
                event: GtmEvent.AUTO_COMPLETE_INPUT_INITIATED,
                category: uniqueIdentifier,
                inputPath: 'microphone',
                userId: user?.id || ''
            });
            toggleVoiceRecognitionPopup();
            if (helperOpened) {
                handleCloseHelper();
            }
        },
        [
            isMobile,
            popover,
            uniqueIdentifier,
            user?.id,
            sendDataToGTM,
            toggleVoiceRecognitionPopup,
            helperOpened,
            onMicClick,
            handleCloseHelper
        ]
    );

    const renderInput = useCallback(
        (params: AutocompleteRenderInputParams, needDict: boolean, sx?: SxProps) => {

            const endAdornment = (
                <>
                    {needDict ? (
                        <IconButton
                            ref={micButtonRef}
                            className="mic_btn"
                            onClick={handleOpenVoice}
                            sx={{
                                position: 'relative',
                                ...(inputVariant === 'main' ? { marginRight: '5px' } : null),
                                zIndex: helperOpened ? 30 : 2,
                                width: inputVariant === 'main' ? '38px' : '59px',
                                bgcolor: 'primary.700',
                                borderRadius: inputVariant === 'main' ? '100%' : '0 7px 7px 0',
                                height: inputVariant === 'main' ? 'auto' : '100%',
                                ':hover': {
                                    background: '#029780'
                                }
                            }}
                        >
                            {<img src={inputVariant === 'main' ? MicrophoneIcon : MicrophoneSolidIcon} alt="" />}
                        </IconButton>
                    ) : (
                        <div style={{
                            width: '38px',
                            height: '100%',
                            borderRadius: '0 7px 7px 0'
                        }} className={`${popover ? 'border-primary border-r-2 border-t-2 border-b-2' : ''} flex items-center justify-center`}>
                            <CustomLoopIcon src={LoopIconBlack} alt="" />
                        </div>
                    )}
                </>
            )

            const startAdornment = needDict ? (
                <InputAdornment
                    position="end"
                    sx={{
                        ...sx,
                        pl: inputVariant === 'advanced' ? '10px' : ''
                    }}
                >
                    {popover ? (
                        <i className="icon-arrow-left cursor-pointer text-2xl text-primary" onClick={onPopoverClose} />
                    ) : (
                        <CustomLoopIcon alt="loop icon" src={LoopIconBlack} />
                    )}
                </InputAdornment>
            ) : popover ? (
                <InputAdornment
                    position="end"
                    sx={{
                        ...sx
                    }}
                >
                    <i className="icon-arrow-left cursor-pointer text-xl text-primary" onClick={onPopoverClose} />
                </InputAdornment>
            ) : null;

            return {
                ...params.InputProps,
                endAdornment,
                startAdornment,
                sx: {
                    '& input:hover': {
                        boxShadow: 'none'
                    }
                }
            };
        },
        [handleOpenVoice, helperOpened, onPopoverClose, popover, inputVariant]
    );

    const onFocus = useCallback(
        (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => {
            if (!isHelperUsed && (!isMobile || popover)) {
                handleShowHelper();
            }

            setIsFocused(true);
            setIsOpen(!!optionsList?.length);

            if (isMobile && !popover) {
                setTimeout(() => e.currentTarget?.blur(), 0);
            }
        },
        [handleShowHelper, isMobile, optionsList, popover, isHelperUsed]
    );

    const onSelectOption = useCallback(
        (event: SyntheticEvent, option: ISelectOption<TOption> | null) => {
            if (inputPath) {
                const differenceInWholeSeconds = Math.floor((new Date().getTime() - inputPath.timestamp.getTime()) / 1000);
                sendDataToGTM({
                    event: GtmEvent.AUTO_COMPLETE_INPUT_SUCCESS,
                    category: uniqueIdentifier,
                    inputPath: inputPath.medium,
                    tDuration: differenceInWholeSeconds,
                    userId: user?.id || ''
                });
                setInputPath(null);
                stringsRef.current = [];
                inputRef.current = null;
                setCollectedStrings([]);
            }

            if (isVoiceInput) {
                sendDataToGTM({
                    event: GtmEvent.VOICE_SEARCH_SUCCESS
                });
            }
            if ((isEmpty && !extraOptions?.length) || !option) return;

            setSearchValue(keepSelectedValue ? option.label : '');
            onChange?.(keepSelectedValue ? option.label : '');
            setIsOpen(false);
            onSelect(option?.value as TOption, option.label);
            onPopoverClose?.();
        },
        [
            inputPath,
            isVoiceInput,
            isEmpty,
            extraOptions?.length,
            keepSelectedValue,
            onChange,
            onSelect,
            onPopoverClose,
            sendDataToGTM,
            uniqueIdentifier,
            user?.id
        ]
    );

    const stringsRef = useRef(collectedStrings);
    const inputRef = useRef(inputPath);

    useEffect(() => {
        stringsRef.current = collectedStrings;
        inputRef.current = inputPath;
    }, [collectedStrings, inputPath]);

    useEffect(() => {
        return () => {
            if (stringsRef?.current?.length && inputRef.current) {
                sendGTMEventUserGaveUp(stringsRef?.current, inputRef.current?.medium);
            }
        };
    }, []);

    const popoverSx = useMemo(
        () =>
            popover
                ? {
                    boxShadow: 'none',
                    overflow: 'initial'
                }
                : {},
        [popover]
    );

    useEffect(() => {
        if (isVoiceRecognitionOpened) {
            intercomContext?.shutDownIntercomContext();
        } else {
            intercomContext?.bootIntercomContext();
        }
    }, [isVoiceRecognitionOpened])

    return (
        <>
            <div className="relative flex flex-col" ref={wrapperRef} data-testid="autocomplete-wrapper">
                <Modal dataTestId="voice-recognition" open={isVoiceRecognitionOpened} onClose={toggleVoiceRecognitionPopup}>
                    <VoiceRecognitionPopup
                        onToKeyboard={toggleVoiceRecognitionPopup}
                        onClose={handleVoiceResult}
                        subject={uniqueIdentifierToUiText[uniqueIdentifier as keyof typeof uniqueIdentifierToUiText]}
                    />
                </Modal>
                {(label || subLabel) && !popover && (
                    <div className="mb-1 flex flex-col gap-1">
                        {label && (
                            <div className={`flex items-center gap-2 ${labelClassName}`}>
                                {required && <span className="text-red-500">*</span>}
                                <span className="text-lg font-bold">{label}</span>
                            </div>
                        )}
                        {subLabel && <span className="mb-[7px] font-dmSans text-sm lg:text-base">{subLabel}</span>}
                    </div>
                )}
                <Autocomplete
                    data-testid="autocomplete-container"
                    ref={autocompleteRef}
                    clearOnBlur={clearOnBlur}
                    sx={{
                        position: 'relative',
                        zIndex: helperOpened ? 100 : 2,
                        padding: '0',
                        '& .MuiInputAdornment-root': {
                            marginLeft: '0px'
                        },

                        borderRadius: '8px',
                        boxShadow: '0px 1px 4px 0px rgba(0, 0, 0, 0.10)',
                        overflow: 'hidden',
                        ...popoverSx
                    }}
                    open={isOpen || isEmpty}
                    options={optionsListFormatted as ISelectOption<TOption>[]}
                    getOptionLabel={(option: ISelectOption<TOption>) => (option.label || '') as string}
                    popupIcon={false}
                    renderOption={(props, option: ISelectOption<TOption>) => (
                        <Box
                            component="li"
                sx={{
                    display: 'none',
                    justifyContent: 'space-between'
                }}
                {...props}
                key={props.id}
                        >
                {option.freeContent || option.label}
                {option.value !== -1 && <img src={PlusIcon} />}
            </Box>
                    )}
            disablePortal
            isOptionEqualToValue={(option, value) => option.value === value.value}
            filterOptions={options => options}
            renderInput={params => (
                <div className="relative">
                    {inputVariant === 'advanced' && popover && (
                        <div className='flex flex-row items-start px-2.5'>
                            <div style={{ width: `${needDict ? '45px' : '35px'}` }} />
                            <h4 className="pb-3 font-dmSansMedium text-xl">{searched ? 'Select from the list below' : popoverTitle}</h4>
                        </div>
                    )}
                    <TextField
                        {...params}
                        autoFocus={popover}
                        placeholder={placeholder}
                        onBlur={() => {
                            !popover && setIsOpen(false);
                            setIsEmpty(false);
                            setIsFocused(false);
                        }}
                        onFocus={onFocus}
                        inputProps={{
                            ...params.inputProps,
                            value: searchValue,
                            style: {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }
                        }}
                        sx={inputSX['advanced']}
                        onChange={handleChange}
                        InputProps={renderInput(params, needDict, startAdornmentSx)}
                    />
                </div>
            )}
            componentsProps={{
                popper: {
                    sx: {
                        top: '0px !important',
                        [theme.breakpoints.down('sm')]: {
                            width: '100vw !important',
                            marginLeft: `-${autocompleteRef.current?.getBoundingClientRect().left}px !important`
                        }
                    }
                },
                paper: {
                    sx: {
                        boxShadow: popover ? 'none' : '0px 4px 4px 2px rgba(0, 0, 0, 0.10)'
                    }
                }
            }}
            ListboxProps={{
                sx: {
                    [theme.breakpoints.down('sm')]: {
                        maxHeight: 'initial'
                    },
                    width: '100%',
                    boxShadow: '0px 4px 4px 2px rgba(0, 0, 0, 0.10)',
                    padding: popover ? '7px 0 0' : 0,
                    bgcolor: popover ? 'white' : '',
                    '& .MuiAutocomplete-option': {
                        display: 'flex',
                        justifyContent: 'space-between',
                        bgcolor: 'white',
                        fontSize: '16px',
                        padding: popover ? '10px 30px 10px 52px' : '10px 20px',
                        wordWrap: 'normal',
                        textDecoration: 'none',
                        '&:hover': {
                            bgcolor: 'primary.50'
                        }
                    }
                }
            }}
                    // disabled={helperOpened}
            onChange={onSelectOption}
            {...props}
                />

            {popover && !searchValue && !optionsListFormatted.length && popoverHelper}
        </div >
        </>
    );
}

export default AutoComplete;
